<template>
  <div v-if="props.isCompletedStep">
    <ShippingAddressCompleted />
  </div>
  <div v-else-if="props.isActiveStep">
    <VcLoaderOverlay :visible="switchingCurrency" fixed-spinner />
    <div v-if="!(addresses.length > 0)" class="-mx-3 md:-mx-5">
      <VcAddressForm
        :model-value="editableAddress"
        :countries="countriesWithoutEmbargoed"
        with-personal-info
        required-email
        required-city
        required-phone
        default-billing-checkbox-hidden
        class="!px-4 !pb-4 !pt-0 md:!px-5"
        @save="saveAddress"
        ><template #append="{ dirty, valid, values, setFieldValue, save }">
          <GstRegistration
            :country-code="values.countryCode"
            :model-value="values.gstinTL"
            @update:model-value="setFieldValue('gstinTL', $event)"
          />
          <div class="mt-4 w-full gap-4 md:-mx-7 md:w-auto md:px-7">
            <TlLoadingButton
              color="primary"
              variant="solid"
              type="button"
              class="mt-3 min-h-[48px] w-full md:w-auto"
              size="lg"
              :disabled="!dirty || !valid"
              :complete-text="$t(`shared.wishlists.list_card.saved`)"
              :enable-complete-text="addressSaved"
              @click="(callback) => submitNewAddress(save, callback)"
            >
              {{ $t("pages.checkout.save_and_continue") }}
            </TlLoadingButton>
          </div>
        </template>
      </VcAddressForm>
    </div>
    <div v-else>
      <div class="md:px-7">
        <div class="selected-address flex flex-col gap-4 md:-mx-7">
          <div
            v-for="(address, index) in addresses"
            :key="index"
            class="selected-address__item flex items-start justify-between"
          >
            <VcRadioButton
              :id="address.id"
              :key="`${address.id}-${deliveryAddressListUpdaterKey}`"
              v-model="userSelectedAddress"
              name="shippingAddress"
              :value="address.id!"
            >
              <ul class="flex max-w-[250px] flex-col font-medium leading-6">
                <!-- Japan-specific address format -->
                <template v-if="address.countryCode === 'JPN'">
                  <li class="mb-2 font-semibold">{{ address.lastName }} {{ address.firstName }}</li>
                  <li>{{ address.postalCode }}</li>
                  <li>{{ address.regionName }}</li>
                  <li>{{ address.city }}</li>
                  <li>{{ address.line1 }}</li>
                  <li v-if="address.buildingTL">{{ address.buildingTL }}</li>
                  <li>{{ address.countryName }}</li>
                  <li class="mt-2">
                    {{ address.phone }}
                  </li>
                  <li class="break-all">{{ address.email }}</li>
                </template>
                <!-- China address format -->
                <template v-else>
                  <li v-if="address.countryCode === 'CHN'" class="mb-2 font-semibold">
                    {{ address.lastName }} {{ address.firstName }}
                  </li>
                  <li v-else class="mb-2 font-semibold">{{ address.firstName }} {{ address.lastName }}</li>
                  <li v-if="address.organization">{{ address.organization }}</li>
                  <li v-if="address.departmentTL">{{ address.departmentTL }}</li>
                  <li v-if="address.buildingTL || address.mailStopTL">
                    {{ address.buildingTL ?? "" }} {{ address.mailStopTL ?? "" }}
                  </li>
                  <li>{{ address.line1 }}</li>
                  <li v-if="isMUCCountry(address.countryCode!)">
                    {{ address.regionName || address.countyTL }} {{ address.postalCode }} {{ address.city }}
                  </li>
                  <li v-else>
                    {{ address.city }}, {{ address.regionName || address.countyTL }} {{ address.postalCode }}
                  </li>
                  <li class="mb-3">{{ address.countryName }}</li>
                  <li>{{ address.phone }}</li>
                  <li class="break-all">{{ address.email }}</li>
                </template>
              </ul>
            </VcRadioButton>
            <VcButton
              type="button"
              variant="link"
              size="lg"
              class="text-[--link-color] underline"
              @click="openAddOrUpdateAddressModal(address, true)"
            >
              {{ $t("common.buttons.edit") }}
            </VcButton>
          </div>

          <div class="flex items-center justify-between">
            <VcButton color="primary" size="lg" variant="link" @click="openAddOrUpdateAddressModal(undefined, true)">
              <VcIcon name="plus-alt" class="mr-2" :size="16"></VcIcon>
              <span class="inline font-semibold">{{ $t("common.buttons.add_new_address") }}</span>
            </VcButton>
          </div>
        </div>
      </div>
      <GstRegistration
        :key="userSelectedAddress"
        v-model="gstin"
        :country-code="selectedCountryCode"
        :disabled="changing"
      />
      <div class="my-4 w-full flex-col gap-4 md:-mx-7 md:w-auto md:px-7">
        <p v-if="!selectedAddressValid" class="mb-2 text-sm text-[--color-danger]">
          {{ $t("pages.checkout.address_has_invalid_fields") }}
        </p>
        <TlLoadingButton
          color="primary"
          variant="solid"
          type="submit"
          class="mt-3 min-h-[48px] w-full min-w-[184px] md:w-auto"
          size="lg"
          :disabled="!userSelectedAddress || changing"
          :enable-complete-text="indiaValidationPassed && addressSaved"
          :complete-text="$t(`shared.wishlists.list_card.saved`)"
          @click="nextStepHandle"
        >
          {{ $t("pages.checkout.save_and_continue") }}
        </TlLoadingButton>
        <p v-if="showInrCountryError" class="mt-2 text-sm text-[--color-danger]">
          {{ $t("pages.checkout.inr_india_shipping_error") }}
        </p>
        <i18n-t
          v-if="showIndiaInrCurrencyError"
          :keypath="
            isInrCurrencyEnabled
              ? 'pages.checkout.india_supported_currency_message'
              : 'pages.checkout.india_supported_currency_message_eur_only'
          "
          tag="p"
          scope="global"
          class="mt-2 text-sm text-[--color-danger]"
        >
          <template v-if="isInrCurrencyEnabled" #inr>
            <button
              type="button"
              class="font-semibold text-[--link-color] underline disabled:opacity-60"
              :disabled="switchingCurrency"
              @click="switchCurrencyAndReturnToCart('INR')"
            >
              INR
            </button>
          </template>
          <template #eur>
            <button
              type="button"
              class="font-semibold text-[--link-color] underline disabled:opacity-60"
              :disabled="switchingCurrency"
              @click="switchCurrencyAndReturnToCart('EUR')"
            >
              EUR
            </button>
          </template>
        </i18n-t>
      </div>
    </div>
    <SuggestedAddressDialog
      v-show="displaySuggestedAddressModal"
      ref="suggestedDialog"
      :suggested-address="suggestedAddress!"
      :user-typed-in-address="userTypedInAddress!"
      :existing-address-edit="existingAddressEdit!"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { ValidationError } from "yup";
import { changeCartCurrency } from "@/core/api/graphql";
import { updateLanguageAndCurrency } from "@/core/api/graphql/account/mutations/updateLanguageAndCurrency";
import { useCountriesWithoutEmbargoed, useCurrency } from "@/core/composables";
import { useDataAreaId } from "@/core/composables/useDataAreaId";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { isMUCCountry } from "@/core/utilities/address";
import { useAddressModal } from "@/shared/account/composables/useAddressModal";
import { useUser } from "@/shared/account/composables/useUser";
import { useUserAddresses } from "@/shared/account/composables/useUserAddresses";
import { INDIA_SUPPORTED_CURRENCIES } from "@/shared/checkout/constants";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { createAddressObjectSchema } from "@/ui-kit/components/molecules/address-form/addressValidationSchemas";
import { useCheckoutFacade } from "../../composables/useCheckoutFacade";
import ShippingAddressCompleted from "./shipping-address-completed.vue";
import type { MemberAddressTypeExtension } from "@/core/api/graphql/types";
import SuggestedAddressDialog from "@/shared/account/components/suggested-address-dialog.vue";
import GstRegistration from "@/shared/checkout/components/gst-registration.vue";
import TlLoadingButton from "@/ui-kit/components/molecules/button/tl-loading-button.vue";

const props = defineProps<IProps>();

interface IProps {
  isCompletedStep?: boolean;
  isActiveStep?: boolean;
  nextStep?: string;
  isLocked?: boolean;
}

const router = useRouter();
const route = useRoute();
const editableAddress = ref<MemberAddressTypeExtension>();
const suggestedDialog = ref<InstanceType<typeof SuggestedAddressDialog> | null>(null);
const displaySuggestedAddressModal = ref(false);
const showDialog = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  displaySuggestedAddressModal.value = true;
  suggestedDialog.value?.showDialog();
};

const { userSelectedAddress, addresses, changing, setCartDeliveryAddress, deliveryAddressListUpdaterKey, cart } =
  useCheckoutFacade();
const { setCountryCode } = useDataAreaId();
const { openAddOrUpdateAddressModal, handleAddressUpdate, suggestedAddress, userTypedInAddress, existingAddressEdit } =
  useAddressModal(showDialog);
const { addOrUpdateAddresses } = useUserAddresses();
const { countriesWithoutEmbargoed } = useCountriesWithoutEmbargoed();
const { currentCurrency, supportedCurrencies, saveCurrencyCode, isInrCurrencyEnabled } = useCurrency();
const { user } = useUser();
const { t } = useI18n();

const selectedAddressValid = ref(true);
const showInrCountryError = ref(false);
const showIndiaInrCurrencyError = ref(false);
const switchingCurrency = ref(false);
const gstin = ref("");
const addressSaved = ref(false);
let addressSave = Promise.resolve();

const addressSchema = computed(() =>
  createAddressObjectSchema({
    t,
    withPersonalInfo: true,
    requiredEmail: true,
    requiredPhone: true,
    requiredCity: true,
    countries: countriesWithoutEmbargoed.value,
  }),
);

const userSelectedAddressDisplay = computed(() => {
  return addresses.value.find((address) => address.id === userSelectedAddress.value);
});

const selectedCountryCode = computed(
  () =>
    userSelectedAddressDisplay.value?.countryCode ??
    addresses.value.find((address) => address.id === userSelectedAddress.value)?.countryCode ??
    "",
);

const indiaValidationPassed = computed(() => {
  if (!isInrCurrencyEnabled.value) {
    return true;
  }

  const countryCode = selectedCountryCode.value;

  const inrOutsideIndia = currentCurrency.value.code === "INR" && countryCode !== "IND";
  const indiaUnsupportedCurrency =
    countryCode === "IND" && !INDIA_SUPPORTED_CURRENCIES.includes(currentCurrency.value.code);

  return !inrOutsideIndia && !indiaUnsupportedCurrency;
});

const saveAddress = (address: MemberAddressTypeExtension) => {
  addressSave = handleAddressUpdate(address, false);
  return addressSave;
};

const submitNewAddress = async (save: () => Promise<unknown>, callback: (result: unknown) => void) => {
  addressSaved.value = false;
  try {
    await save();
    await addressSave;
    addressSaved.value = true;
  } catch (error) {
    Logger.error("Failed to save shipping address", error);
    throw error;
  } finally {
    callback(undefined);
  }
};

const switchCurrencyAndReturnToCart = async (currencyCode: string) => {
  const targetCurrency = supportedCurrencies.value.find((currency) => currency.code === currencyCode);
  if (!targetCurrency) {
    return;
  }

  switchingCurrency.value = true;

  try {
    localStorage.setItem("previousCurrency", globals.currencyCode);

    await updateLanguageAndCurrency({
      userId: user.value?.id,
      language: user.value?.contact?.id ? user.value?.contact.defaultLanguage : globals.cultureName,
      currency: targetCurrency.code,
    });

    if (cart.value) {
      await changeCartCurrency(cart.value.id, targetCurrency.code);
    }

    // Reloads the page; the loader stays until the cart renders after the route guard redirect.
    saveCurrencyCode(targetCurrency.code);
  } catch (error) {
    Logger.error(`${switchCurrencyAndReturnToCart.name}: failed to switch currency to ${currencyCode}`, error);
    await router.push({ name: "Cart", replace: true }).catch(() => {});
    switchingCurrency.value = false;
  }
};

const nextStepHandle = async (callback: (result: unknown) => void) => {
  addressSaved.value = false;
  try {
    const countryCode = selectedCountryCode.value;

    showInrCountryError.value = false;
    showIndiaInrCurrencyError.value = false;
    if (isInrCurrencyEnabled.value) {
      if (currentCurrency.value.code === "INR" && countryCode !== "IND") {
        showInrCountryError.value = true;
        return;
      }

      // India only supports INR or EUR payments; surface the notice with clickable INR/EUR links
      // instead of advancing.
      if (countryCode === "IND" && !INDIA_SUPPORTED_CURRENCIES.includes(currentCurrency.value.code)) {
        showIndiaInrCurrencyError.value = true;
        return;
      }
    }

    setCountryCode(countryCode);

    const selectedAddress = userSelectedAddressDisplay.value;
    if (selectedAddress) {
      const updatedAddress = { ...selectedAddress, gstinTL: countryCode === "IND" ? gstin.value : "" };
      if ((selectedAddress.gstinTL ?? "") !== updatedAddress.gstinTL) {
        await addOrUpdateAddresses([updatedAddress]);
      }
      await setCartDeliveryAddress(updatedAddress);
    }
    addressSaved.value = true;

    if (props.nextStep) {
      await router.push({ name: props.nextStep, query: route.query, replace: true });

      // Wait for DOM update, then scroll next step into view with header offset
      requestAnimationFrame(() => {
        const activeStepEl = document.querySelector('[data-step="active"]') as HTMLElement;
        if (activeStepEl) {
          const headerOffset = window.innerWidth >= 768 ? 100 : 75; // <-- adjust mobile value if needed
          const elementPosition = activeStepEl.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      });
    }
  } catch (error) {
    Logger.error("Failed to save shipping address", error);
    throw error;
  } finally {
    callback(undefined);
  }
};

watch(addresses, (newAddresses: MemberAddressTypeExtension[]) => {
  if (props.isActiveStep) {
    const mostRecentAddress = newAddresses.reduce(
      (latest: MemberAddressTypeExtension, address: MemberAddressTypeExtension) => {
        return new Date(address.modifiedDate) > new Date(latest.modifiedDate) ? address : latest;
      },
      newAddresses[0],
    );
    userSelectedAddress.value = mostRecentAddress.id;
  }
});

watch(
  userSelectedAddressDisplay,
  async (newAddress?: MemberAddressTypeExtension) => {
    gstin.value = newAddress?.gstinTL ?? "";
    if (newAddress && props.isActiveStep) {
      showInrCountryError.value = false;
      showIndiaInrCurrencyError.value = false;
      if (newAddress.taxExemptNoTL) {
        eventBus.emit("hasTaxExemptNumber", { hasTaxExemptNumber: true });
      } else {
        eventBus.emit("hasTaxExemptNumber", { hasTaxExemptNumber: false });
      }

      try {
        await addressSchema.value.validate(newAddress, { abortEarly: true });
        selectedAddressValid.value = true;
      } catch (error) {
        selectedAddressValid.value = !(error instanceof ValidationError);
      }

      await setCartDeliveryAddress(newAddress);
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
.selected-address {
  .vc-radio-button {
    @apply items-start;

    .vc-radio-button__input {
      @apply mt-1;
    }
  }
  .vc-radio-button--checked {
    .vc-radio-button__input {
      @apply accent-[--color-link];
    }
  }
}
div:has(.faq-btn) {
  @apply mb-0;
}
</style>
